import { useState, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { ca, es } from 'date-fns/locale';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import LanguageSelector from '../components/LanguageSelector';
import { Button } from '../components/ui/button';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import {
  CalendarIcon, LogOut, ClipboardList, Cat, Syringe, Scissors,
  Download, Upload, Trash2, Phone, ArrowLeft, AlertCircle, Search,
  FileSpreadsheet, FileText, Pencil, Server, X,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import {
  listAdoptions, deleteAdoption, exportAdoptions, importAdoptions, computePending,
  exportPendingCSV,
} from '../lib/adoptionStorage';
import { downloadAdoptionPDF, getAdoptionPdfBlob } from '../lib/pdfGenerator';
import { getNasConfig, saveNasConfig, uploadContractPdf, testNasConnection } from '../lib/nasUpload';
import EditAdoptionModal from '../components/EditAdoptionModal';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

export default function ControlPage() {
  const { logout } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const locale = language === 'es' ? es : ca;
  const fileInputRef = useRef(null);

  const [targetDate, setTargetDate] = useState(new Date());
  const [filter, setFilter] = useState('all'); // 'all' | 'sterilization' | 'vaccination'
  const [horizon, setHorizon] = useState(90); // dies de vista endavant. 0 = només retards
  const [query, setQuery] = useState('');
  const [birthMonth, setBirthMonth] = useState('all'); // 'all' o '01'..'12'
  const [records, setRecords] = useState(() => listAdoptions());
  const [editing, setEditing] = useState(null); // record en edició
  const [showNasConfig, setShowNasConfig] = useState(false);
  const [nasConfig, setNasConfig] = useState(() => getNasConfig());

  const refresh = () => setRecords(listAdoptions());

  // També incloem "properes" (encara no pendent, però dins horitzó) → daysDiff negatiu i > -horizon
  const sterInHorizon = (r) => {
    if (r.sterilizationDueDate == null && r.sterilizationIndeterminate) return true;
    if (r.sterilizationDueDate == null) return false;
    return r.sterilizationDaysDiff >= -horizon;
  };
  const vacInHorizon = (r) => {
    if (r.vaccinationDueDate == null && r.vaccinationIndeterminate) return true;
    if (r.vaccinationDueDate == null) return false;
    return r.vaccinationDaysDiff >= -horizon;
  };

  const pending = useMemo(() => {
    const q = query.trim().toLowerCase();
    return records
      .map(r => ({ ...r, ...computePending(r, targetDate) }))
      .filter(r => {
        const sInScope = sterInHorizon(r);
        const vInScope = vacInHorizon(r);
        if (filter === 'sterilization' && !sInScope) return false;
        if (filter === 'vaccination' && !vInScope) return false;
        if (filter === 'all' && !sInScope && !vInScope) return false;
        if (q) {
          const hay = `${r.nombre_gato} ${r.nombre} ${r.apellidos} ${r.microchip}`.toLowerCase();
          if (!hay.includes(q)) return false;
        }
        if (birthMonth !== 'all') {
          // data_naixement en format dd/MM/yyyy
          const parts = (r.data_naixement || '').split('/');
          if (parts.length !== 3 || parts[1] !== birthMonth) return false;
        }
        return true;
      })
      .sort((a, b) => {
        // Ordena per la data més propera/vençuda:
        // sortKey = max daysDiff (indeterminats compten com "molt urgent" → +Infinity)
        const scoreOf = (r) => {
          const scores = [];
          if (r.sterilizationIndeterminate) scores.push(Number.POSITIVE_INFINITY);
          else if (r.sterilizationDaysDiff != null && sterInHorizon(r)) scores.push(r.sterilizationDaysDiff);
          if (r.vaccinationIndeterminate) scores.push(Number.POSITIVE_INFINITY);
          else if (r.vaccinationDaysDiff != null && vacInHorizon(r)) scores.push(r.vaccinationDaysDiff);
          return scores.length ? Math.max(...scores) : Number.NEGATIVE_INFINITY;
        };
        return scoreOf(b) - scoreOf(a); // descendent: més positiu (retard) primer, més negatiu (llunyà) al final
      });
  }, [records, targetDate, filter, horizon, query, birthMonth]);

  const handleLogout = () => { logout(); navigate('/'); };

  const handleExport = () => {
    if (records.length === 0) {
      toast.error(t('control_export_empty'));
      return;
    }
    exportAdoptions();
    toast.success(t('control_export_ok'));
  };

  const handleImportClick = () => fileInputRef.current?.click();

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const total = await importAdoptions(file, { merge: true });
      refresh();
      toast.success(t('control_import_ok').replace('{n}', total));
    } catch (err) {
      toast.error(t('control_import_err'));
    } finally {
      e.target.value = '';
    }
  };

  const handleDelete = (id) => {
    if (!window.confirm(t('control_delete_confirm'))) return;
    deleteAdoption(id);
    refresh();
    toast.success(t('control_deleted'));
  };

  const formatDMY = (d) => `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;

  const handleDownloadContract = async (record) => {
    try {
      const data = record.formDataSnapshot || record;
      await downloadAdoptionPDF(data, language);
      toast.success(t('control_contract_ok'));
    } catch (e) {
      toast.error(t('control_contract_err'));
    }
  };

  const handleUploadNas = async (record) => {
    const cfg = getNasConfig();
    if (!cfg.enabled || !cfg.baseUrl) {
      toast.error(t('nas_not_configured'));
      setShowNasConfig(true);
      return;
    }
    try {
      const data = record.formDataSnapshot || record;
      const blob = await getAdoptionPdfBlob(data, language);
      const filename = `contracte_${(record.nombre_gato || 'gat').replace(/[^\w-]+/g, '_')}_${record.id.split('-')[0]}.pdf`;
      const res = await uploadContractPdf(blob, filename);
      if (res.ok) toast.success(t('nas_upload_ok').replace('{f}', filename));
      else toast.error(t('nas_upload_err') + (res.status ? ` (${res.status})` : ''));
    } catch (e) {
      toast.error(t('nas_upload_err'));
    }
  };

  const handleSaveNasConfig = () => {
    const saved = saveNasConfig(nasConfig);
    setNasConfig(saved);
    toast.success(t('nas_config_saved'));
    setShowNasConfig(false);
  };

  const handleTestNas = async () => {
    // Guarda temporalment per fer el test amb la config actual
    saveNasConfig(nasConfig);
    const res = await testNasConnection();
    if (res.ok) toast.success(t('nas_test_ok'));
    else toast.error(`${t('nas_test_err')} ${res.status || res.error || ''}`);
  };

  const handleExportCsv = () => {
    if (pending.length === 0) {
      toast.error(t('control_csv_empty'));
      return;
    }
    exportPendingCSV(pending, {
      nombre_gato: t('control_csv_cat'),
      microchip: t('control_csv_chip'),
      adopter: t('control_csv_adopter'),
      telefono: t('control_csv_phone'),
      email: t('control_csv_email'),
      sterilization_status: t('control_csv_ster_status'),
      sterilization_due: t('control_csv_ster_due'),
      sterilization_days: t('control_csv_ster_days'),
      vaccination_status: t('control_csv_vac_status'),
      vaccination_due: t('control_csv_vac_due'),
      vaccination_days: t('control_csv_vac_days'),
      last_vaccination: t('control_csv_last_vac'),
      sterilization_date: t('control_csv_ster_date'),
      data_naixement: t('control_csv_birth'),
      fecha_adopcion: t('control_csv_adoption'),
      status_overdue: t('control_csv_status_overdue'),
      status_today: t('control_csv_status_today'),
      status_upcoming: t('control_csv_status_upcoming'),
      status_indeterminate: t('control_csv_status_indeterminate'),
      status_done: t('control_csv_status_done'),
      yes: t('form_yes'),
      no: t('form_no'),
    });
    toast.success(t('control_csv_ok'));
  };

  const stats = {
    total: records.length,
    sterilization: pending.filter(r => sterInHorizon(r)).length,
    vaccination: pending.filter(r => vacInHorizon(r)).length,
  };

  return (
    <div className="min-h-screen bg-stone-50" data-testid="control-page">
      <header className="px-8 py-6 border-b border-stone-200 bg-white flex items-center justify-between">
        <div className="flex items-center gap-4">
          <img src="/logo.jpg" alt="Gats del Carrer" className="h-12 w-auto" />
          <div>
            <p className="text-xs uppercase tracking-widest text-stone-500">{t('control_page_kicker')}</p>
            <h1 className="text-xl font-bold text-stone-900 flex items-center gap-2">
              <ClipboardList size={20} className="text-orange-600" />
              {t('control_page_title')}
            </h1>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/app')} className="text-stone-600 hover:text-stone-900" data-testid="back-to-form-btn">
            <ArrowLeft size={16} className="mr-2" />{t('control_back_form')}
          </Button>
          <LanguageSelector variant="compact" />
          <Button variant="ghost" size="sm" onClick={handleLogout} className="text-stone-500 hover:text-stone-700" data-testid="logout-btn">
            <LogOut size={16} className="mr-2" />{t('logout')}
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-8 py-8 space-y-6">
        {/* Controls */}
        <div className="bg-white rounded-3xl border border-stone-100 p-6 shadow-sm">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
            {/* Data objectiu */}
            <div>
              <label className="text-sm font-medium text-stone-700 flex items-center gap-2 mb-2">
                <CalendarIcon size={14} /> {t('control_target_date')}
              </label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className={cn('w-full justify-start text-left font-normal rounded-xl border-stone-200 bg-stone-50 hover:bg-stone-100')} data-testid="target-date-btn">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {format(targetDate, 'PPP', { locale })}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar mode="single" selected={targetDate} onSelect={(d) => d && setTargetDate(d)} initialFocus />
                </PopoverContent>
              </Popover>
            </div>

            {/* Filtre */}
            <div>
              <label className="text-sm font-medium text-stone-700 mb-2 block">{t('control_filter')}</label>
              <div className="flex gap-2 flex-wrap">
                <Button size="sm" variant={filter === 'all' ? 'default' : 'outline'} onClick={() => setFilter('all')} className={cn('rounded-full', filter === 'all' && 'bg-orange-600 hover:bg-orange-700')} data-testid="filter-all">
                  {t('control_filter_all')}
                </Button>
                <Button size="sm" variant={filter === 'sterilization' ? 'default' : 'outline'} onClick={() => setFilter('sterilization')} className={cn('rounded-full', filter === 'sterilization' && 'bg-orange-600 hover:bg-orange-700')} data-testid="filter-sterilization">
                  <Scissors size={13} className="mr-1" />{t('control_filter_sterilization')}
                </Button>
                <Button size="sm" variant={filter === 'vaccination' ? 'default' : 'outline'} onClick={() => setFilter('vaccination')} className={cn('rounded-full', filter === 'vaccination' && 'bg-orange-600 hover:bg-orange-700')} data-testid="filter-vaccination">
                  <Syringe size={13} className="mr-1" />{t('control_filter_vaccination')}
                </Button>
              </div>
            </div>

            {/* Cerca */}
            <div>
              <label className="text-sm font-medium text-stone-700 mb-2 block">{t('control_search')}</label>
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={t('control_search_ph')}
                  className="w-full pl-9 pr-3 py-2 rounded-xl border border-stone-200 bg-stone-50 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  data-testid="control-search-input"
                />
              </div>
              <div className="mt-2">
                <select
                  value={birthMonth}
                  onChange={(e) => setBirthMonth(e.target.value)}
                  className="w-full px-3 py-2 rounded-xl border border-stone-200 bg-stone-50 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  data-testid="birth-month-select"
                >
                  <option value="all">{t('control_birth_month_all')}</option>
                  {['01','02','03','04','05','06','07','08','09','10','11','12'].map(m => (
                    <option key={m} value={m}>{t(`month_${m}`)}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Horitzó (vista de dies) */}
          <div className="mt-4 pt-4 border-t border-stone-100">
            <label className="text-sm font-medium text-stone-700 mb-2 block">{t('control_horizon')}</label>
            <div className="flex gap-2 flex-wrap">
              {[
                { v: 0, k: 'control_horizon_overdue' },
                { v: 30, k: 'control_horizon_30' },
                { v: 90, k: 'control_horizon_90' },
                { v: 365, k: 'control_horizon_365' },
              ].map(opt => (
                <Button
                  key={opt.v}
                  size="sm"
                  variant={horizon === opt.v ? 'default' : 'outline'}
                  onClick={() => setHorizon(opt.v)}
                  className={cn('rounded-full', horizon === opt.v && 'bg-stone-900 hover:bg-stone-800')}
                  data-testid={`horizon-${opt.v}`}
                >
                  {t(opt.k)}
                </Button>
              ))}
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-3 mt-6 pt-6 border-t border-stone-100">
            <div className="text-center">
              <p className="text-2xl font-bold text-stone-900">{stats.total}</p>
              <p className="text-xs text-stone-500 uppercase tracking-wide">{t('control_stat_total')}</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-orange-600">{stats.sterilization}</p>
              <p className="text-xs text-stone-500 uppercase tracking-wide">{t('control_stat_sterilization')}</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-blue-600">{stats.vaccination}</p>
              <p className="text-xs text-stone-500 uppercase tracking-wide">{t('control_stat_vaccination')}</p>
            </div>
          </div>

          {/* Export / Import */}
          <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-stone-100">
            <Button size="sm" variant="outline" onClick={handleExportCsv} className="rounded-full bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100 hover:text-emerald-800" data-testid="export-csv-btn">
              <FileSpreadsheet size={14} className="mr-2" />{t('control_export_csv')}
            </Button>
            <Button size="sm" variant="outline" onClick={handleExport} className="rounded-full" data-testid="export-btn">
              <Download size={14} className="mr-2" />{t('control_export')}
            </Button>
            <Button size="sm" variant="outline" onClick={handleImportClick} className="rounded-full" data-testid="import-btn">
              <Upload size={14} className="mr-2" />{t('control_import')}
            </Button>
            <Button size="sm" variant="outline" onClick={() => setShowNasConfig(true)} className={cn('rounded-full', nasConfig.enabled && 'bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100')} data-testid="nas-config-btn">
              <Server size={14} className="mr-2" />{nasConfig.enabled ? t('nas_configured') : t('nas_configure')}
            </Button>
            <input ref={fileInputRef} type="file" accept="application/json" onChange={handleImport} className="hidden" data-testid="import-file-input" />
          </div>
        </div>

        {/* Llista */}
        <div className="bg-white rounded-3xl border border-stone-100 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-stone-100 flex items-center justify-between">
            <h2 className="font-semibold text-stone-900 flex items-center gap-2">
              <AlertCircle size={16} className="text-orange-600" />
              {t('control_results')} <span className="text-stone-400 font-normal">({pending.length})</span>
            </h2>
          </div>

          {pending.length === 0 ? (
            <div className="p-12 text-center">
              <Cat size={40} className="mx-auto text-stone-300 mb-4" />
              <p className="text-stone-500 text-sm">
                {records.length === 0 ? t('control_no_records') : t('control_no_pending')}
              </p>
            </div>
          ) : (
            <ul className="divide-y divide-stone-100" data-testid="pending-list">
              {pending.map(r => (
                <li key={r.id} className="p-5 hover:bg-stone-50/50 transition-colors" data-testid={`pending-item-${r.id}`}>
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Cat size={16} className="text-orange-600 flex-shrink-0" />
                        <h3 className="font-semibold text-stone-900 truncate">{r.nombre_gato || '—'}</h3>
                        {r.microchip && <span className="text-xs text-stone-400 font-mono">#{r.microchip}</span>}
                      </div>
                      <p className="text-sm text-stone-600">
                        <span className="text-stone-400">{t('control_adopter')}:</span> {r.nombre} {r.apellidos}
                      </p>
                      {r.telefono && (
                        <a href={`tel:${r.telefono}`} className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline mt-1">
                          <Phone size={12} />{r.telefono}
                        </a>
                      )}

                      <div className="flex flex-wrap gap-2 mt-3">
                        {sterInHorizon(r) && (() => {
                          const overdue = r.sterilizationPending && r.sterilizationDaysDiff != null && r.sterilizationDaysDiff > 0;
                          const today = r.sterilizationDaysDiff === 0;
                          const upcoming = r.sterilizationDaysDiff != null && r.sterilizationDaysDiff < 0;
                          const indet = r.sterilizationIndeterminate;
                          const cls = overdue
                            ? 'bg-orange-50 text-orange-700 border-orange-200'
                            : today ? 'bg-amber-50 text-amber-700 border-amber-200'
                            : upcoming ? 'bg-stone-50 text-stone-600 border-stone-200'
                            : 'bg-stone-100 text-stone-500 border-stone-200';
                          return (
                            <span className={cn('inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border', cls)}>
                              <Scissors size={11} />
                              {overdue ? t('control_pending_sterilization') : today ? t('control_due_today') : upcoming ? t('control_upcoming_sterilization') : t('control_pending_sterilization')}
                              {indet ? (
                                <span className="opacity-70">· {t('control_no_data')}</span>
                              ) : overdue ? (
                                <span className="text-orange-500">· +{r.sterilizationDaysDiff}d</span>
                              ) : upcoming ? (
                                <span className="text-stone-500">· {t('control_in')} {Math.abs(r.sterilizationDaysDiff)}d</span>
                              ) : null}
                            </span>
                          );
                        })()}
                        {vacInHorizon(r) && (() => {
                          const overdue = r.vaccinationPending && r.vaccinationDaysDiff != null && r.vaccinationDaysDiff > 0;
                          const today = r.vaccinationDaysDiff === 0;
                          const upcoming = r.vaccinationDaysDiff != null && r.vaccinationDaysDiff < 0;
                          const indet = r.vaccinationIndeterminate;
                          const cls = overdue
                            ? 'bg-blue-50 text-blue-700 border-blue-200'
                            : today ? 'bg-amber-50 text-amber-700 border-amber-200'
                            : upcoming ? 'bg-stone-50 text-stone-600 border-stone-200'
                            : 'bg-stone-100 text-stone-500 border-stone-200';
                          return (
                            <span className={cn('inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border', cls)}>
                              <Syringe size={11} />
                              {overdue ? t('control_pending_vaccination') : today ? t('control_due_today') : upcoming ? t('control_upcoming_vaccination') : t('control_pending_vaccination')}
                              {indet ? (
                                <span className="opacity-70">· {t('control_no_data')}</span>
                              ) : overdue ? (
                                <span className="text-blue-500">· +{r.vaccinationDaysDiff}d</span>
                              ) : upcoming ? (
                                <span className="text-stone-500">· {t('control_in')} {Math.abs(r.vaccinationDaysDiff)}d</span>
                              ) : null}
                            </span>
                          );
                        })()}
                      </div>

                      {/* Dates de venciment */}
                      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs">
                        {r.sterilizationDueDate && !r.sterilizationIndeterminate && sterInHorizon(r) && (
                          <span className="text-stone-500">
                            <Scissors size={10} className="inline mr-1" />
                            {t('control_due')}: <span className="text-stone-800 font-medium">{format(r.sterilizationDueDate, 'dd/MM/yyyy')}</span>
                          </span>
                        )}
                        {r.vaccinationDueDate && !r.vaccinationIndeterminate && vacInHorizon(r) && (
                          <span className="text-stone-500">
                            <Syringe size={10} className="inline mr-1" />
                            {t('control_due')}: <span className="text-stone-800 font-medium">{format(r.vaccinationDueDate, 'dd/MM/yyyy')}</span>
                          </span>
                        )}
                      </div>

                      <div className="grid grid-cols-2 gap-2 mt-3 text-xs text-stone-500">
                        <div>
                          <span className="text-stone-400">{t('control_last_vaccination')}:</span>{' '}
                          <span className="text-stone-700">{r.lastVaccinationDate ? format(r.lastVaccinationDate, 'dd/MM/yyyy') : '—'}</span>
                        </div>
                        <div>
                          <span className="text-stone-400">{t('control_sterilization_date')}:</span>{' '}
                          <span className="text-stone-700">{r.sterilizationDate ? format(r.sterilizationDate, 'dd/MM/yyyy') : (r.esterilitzat === 'si' ? t('form_yes') : '—')}</span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-1 self-start flex-wrap justify-end">
                      <Button size="sm" onClick={() => setEditing(r)} className="rounded-full bg-orange-600 hover:bg-orange-700 text-white text-xs h-8 px-3" data-testid={`edit-${r.id}`}>
                        <Pencil size={12} className="mr-1.5" />{t('control_edit')}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => handleDownloadContract(r)} className="text-stone-400 hover:text-emerald-600" title={t('control_download_contract')} data-testid={`contract-${r.id}`}>
                        <FileText size={14} />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => handleUploadNas(r)} className={cn('text-stone-400 hover:text-blue-600', !nasConfig.enabled && 'opacity-40')} title={nasConfig.enabled ? t('control_upload_nas') : t('nas_not_configured')} data-testid={`nas-${r.id}`}>
                        <Server size={14} />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => handleDelete(r.id)} className="text-stone-400 hover:text-red-600" data-testid={`delete-${r.id}`}>
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </main>

      {editing && (
        <EditAdoptionModal
          record={editing}
          onClose={() => setEditing(null)}
          onSaved={refresh}
        />
      )}

      {showNasConfig && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={() => setShowNasConfig(false)} data-testid="nas-modal">
          <div className="bg-white rounded-3xl w-full max-w-lg shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-stone-100 flex items-center justify-between">
              <h2 className="font-bold text-stone-900 flex items-center gap-2">
                <Server size={18} className="text-blue-600" /> {t('nas_config_title')}
              </h2>
              <Button variant="ghost" size="sm" onClick={() => setShowNasConfig(false)}>
                <X size={16} />
              </Button>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-xs text-stone-500 leading-relaxed">{t('nas_config_help')}</p>

              <div className="flex items-center justify-between p-3 rounded-xl bg-stone-50 border border-stone-200">
                <div>
                  <Label className="text-sm font-medium">{t('nas_enabled')}</Label>
                  <p className="text-xs text-stone-500">{t('nas_enabled_help')}</p>
                </div>
                <input
                  type="checkbox"
                  checked={nasConfig.enabled}
                  onChange={(e) => setNasConfig({ ...nasConfig, enabled: e.target.checked })}
                  className="h-5 w-5 accent-blue-600"
                  data-testid="nas-enabled-toggle"
                />
              </div>

              <div>
                <Label className="text-xs text-stone-600 mb-1 block">{t('nas_url')}</Label>
                <Input value={nasConfig.baseUrl} onChange={(e) => setNasConfig({ ...nasConfig, baseUrl: e.target.value })} placeholder="https://nas.exemple.com/webdav" className="text-sm h-9 rounded-lg" data-testid="nas-url" />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-stone-600 mb-1 block">{t('nas_username')}</Label>
                  <Input value={nasConfig.username} onChange={(e) => setNasConfig({ ...nasConfig, username: e.target.value })} className="text-sm h-9 rounded-lg" data-testid="nas-username" />
                </div>
                <div>
                  <Label className="text-xs text-stone-600 mb-1 block">{t('nas_password')}</Label>
                  <Input type="password" value={nasConfig.password} onChange={(e) => setNasConfig({ ...nasConfig, password: e.target.value })} className="text-sm h-9 rounded-lg" data-testid="nas-password" />
                </div>
              </div>

              <div>
                <Label className="text-xs text-stone-600 mb-1 block">{t('nas_folder')}</Label>
                <Input value={nasConfig.folder} onChange={(e) => setNasConfig({ ...nasConfig, folder: e.target.value })} placeholder="contractes" className="text-sm h-9 rounded-lg" data-testid="nas-folder" />
              </div>

              <div className="p-3 rounded-xl bg-amber-50 border border-amber-200 text-xs text-amber-800">
                <strong>⚠️ {t('nas_security_title')}:</strong> {t('nas_security_help')}
              </div>
            </div>
            <div className="px-6 py-4 border-t border-stone-100 flex justify-between gap-2">
              <Button variant="outline" onClick={handleTestNas} data-testid="nas-test-btn">{t('nas_test')}</Button>
              <div className="flex gap-2">
                <Button variant="ghost" onClick={() => setShowNasConfig(false)}>{t('edit_cancel')}</Button>
                <Button onClick={handleSaveNasConfig} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="nas-save-btn">
                  {t('edit_save')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
